import { useTenantBackends, useTriggerOnboard, useTenant, usePatchTenantSettings, useSupportGrants, useCreateSupportGrant, useRevokeSupportGrant } from '@/api/admin'
import { RequirePermission } from '@/components/RequirePermission'
import { useRoles } from '@/hooks/usePermission'
import { useState, useEffect } from 'react'
import type { SupportGrantSection } from '@/types'

interface Props {
  tenantId: string | null
}

const ALL_SECTIONS: { value: SupportGrantSection; label: string }[] = [
  { value: 'devices',     label: 'Devices' },
  { value: 'fleets',      label: 'Fleets' },
  { value: 'credentials', label: 'SSH Credentials' },
  { value: 'ota_updates', label: 'OTA Updates' },
  { value: 'playbooks',   label: 'Playbooks' },
]

const card: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius)',
  padding: 20,
  marginBottom: 24,
}

const label: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: 4,
}


function StatusBadge({ onboardedAt, error }: { onboardedAt?: string; error?: string }) {
  if (error) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: 'rgba(220,38,38,0.12)', color: '#f87171',
        border: '1px solid rgba(220,38,38,0.3)',
        borderRadius: 'var(--radius-sm)', padding: '3px 10px', fontSize: 12, fontWeight: 600,
      }}>
        ● Error
      </span>
    )
  }
  if (onboardedAt) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: 'rgba(34,197,94,0.12)', color: '#4ade80',
        border: '1px solid rgba(34,197,94,0.3)',
        borderRadius: 'var(--radius-sm)', padding: '3px 10px', fontSize: 12, fontWeight: 600,
      }}>
        ● Onboarded
      </span>
    )
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'rgba(234,179,8,0.12)', color: '#facc15',
      border: '1px solid rgba(234,179,8,0.3)',
      borderRadius: 'var(--radius-sm)', padding: '3px 10px', fontSize: 12, fontWeight: 600,
    }}>
      ● Pending
    </span>
  )
}

function BackendRow({ label: lbl, value }: { label: string; value?: string }) {
  if (!value) return null
  return (
    <tr>
      <td style={{ color: 'var(--color-text-muted)', fontSize: 12, paddingRight: 16, whiteSpace: 'nowrap' }}>{lbl}</td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{value}</td>
    </tr>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function TenantStatus({ tenantId }: Props) {
  const { data: tb, isLoading, error } = useTenantBackends(tenantId)
  const triggerOnboard = useTriggerOnboard(tenantId ?? '')
  const { data: tenant } = useTenant(tenantId)
  const patchSettings = usePatchTenantSettings(tenantId ?? '')
  const [swupdateKey, setSwupdateKey] = useState('')
  const [keySaved, setKeySaved] = useState(false)
  const roles = useRoles()
  const isPlatformAdmin = roles.includes('platform-admin')
  const isTenantAdmin = roles.includes('tenant-admin')

  useEffect(() => {
    setSwupdateKey((tenant?.settings?.swupdate_public_key as string) ?? '')
  }, [tenant?.settings?.swupdate_public_key])

  if (!tenantId) {
    return <div className="empty-state">Select a tenant to view its status.</div>
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Tenant Status</h1>
          <p className="page-subtitle">Onboarding state and backend resources</p>
        </div>
      </div>

      {/* ── Onboarding status ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ ...label, marginBottom: 8 }}>Onboarding status</div>
            {isLoading && <span style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Loading…</span>}
            {error && <span style={{ color: 'var(--color-error)', fontSize: 13 }}>Failed to load status</span>}
            {tb && <StatusBadge onboardedAt={tb.onboarded_at} error={tb.onboarding_error} />}
          </div>
          <RequirePermission perm="admin:access">
            <button
              className="btn btn-ghost"
              style={{ fontSize: 12, whiteSpace: 'nowrap' }}
              disabled={triggerOnboard.isPending}
              onClick={() => triggerOnboard.mutate()}
            >
              {triggerOnboard.isPending ? 'Running…' : '↺ Re-trigger onboarding'}
            </button>
          </RequirePermission>
        </div>

        {tb?.onboarded_at && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--color-text-muted)' }}>
            Completed at: {new Date(tb.onboarded_at).toLocaleString()}
          </div>
        )}

        {tb?.onboarding_error && (
          <div style={{
            marginTop: 12,
            background: 'rgba(220,38,38,0.08)',
            border: '1px solid rgba(220,38,38,0.25)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#f87171', textTransform: 'uppercase', marginBottom: 6 }}>
              Error details
            </div>
            <pre style={{
              margin: 0,
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              color: '#fca5a5',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}>
              {tb.onboarding_error}
            </pre>
          </div>
        )}

        {triggerOnboard.isError && (
          <div style={{ marginTop: 10, color: 'var(--color-error)', fontSize: 12 }}>
            Re-trigger failed. Check the console for details.
          </div>
        )}
      </div>

      {/* ── Backend resource IDs — platform-admin only ── */}
      {isPlatformAdmin && tb && (tb.awx_organization_id || tb.gitea_org_name) && (
        <div style={card}>
          <div style={{ ...label, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            Backend resources
            <span style={{
              fontSize: 10, fontWeight: 600, letterSpacing: '0.06em',
              padding: '2px 6px', borderRadius: 3,
              background: 'rgba(0,229,182,0.1)', color: 'var(--color-accent)',
              border: '1px solid rgba(0,229,182,0.2)',
              textTransform: 'uppercase',
            }}>
              Superadmin
            </span>
          </div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              <BackendRow label="AWX Organization ID" value={tb.awx_organization_id} />
              <BackendRow label="AWX Inventory ID" value={tb.awx_inventory_id} />
              <BackendRow label="AWX Project ID" value={tb.awx_project_id} />
              <BackendRow label="AWX Bootstrap Template ID" value={tb.awx_bootstrap_template_id} />
              <BackendRow label="Gitea Org" value={tb.gitea_org_name} />
              <BackendRow label="Gitea Repo" value={tb.gitea_repo_name} />
              <BackendRow label="Gitea Repo URL" value={tb.gitea_repo_url} />
              <BackendRow label="HawkBit Tenant" value={tb.hawkbit_tenant_name} />
            </tbody>
          </table>
        </div>
      )}

      {/* ── swupdate signing certificate — tenant-admin only ── */}
      {isTenantAdmin && !isPlatformAdmin && tenantId && (
        <div style={card}>
          <div style={{ ...label, marginBottom: 12 }}>swupdate signing certificate (CMS/PEM)</div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 12 }}>
            Incolla il certificato X.509 PEM usato per verificare le immagini .swu firmate con CMS.
            Viene iniettato come AWX extra variable (<code style={{ fontFamily: 'var(--font-mono)', color: '#94a3b8' }}>swupdate_public_key</code>)
            ad ogni bootstrap.
          </div>
          <textarea
            rows={6}
            style={{
              width: '100%',
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--color-text)',
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              padding: '8px 10px',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
            placeholder="-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"
            value={swupdateKey}
            onChange={e => { setSwupdateKey(e.target.value); setKeySaved(false) }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
            <button
              className="btn btn-primary"
              style={{ fontSize: 12 }}
              disabled={patchSettings.isPending}
              onClick={() =>
                patchSettings.mutate(
                  { swupdate_public_key: swupdateKey },
                  { onSuccess: () => setKeySaved(true) },
                )
              }
            >
              {patchSettings.isPending ? 'Saving…' : 'Save key'}
            </button>
            {keySaved && <span style={{ fontSize: 12, color: '#4ade80' }}>Saved ✓</span>}
            {patchSettings.isError && (
              <span style={{ fontSize: 12, color: 'var(--color-error)' }}>Save failed</span>
            )}
          </div>
          <details style={{ marginTop: 14 }}>
            <summary style={{ fontSize: 11, color: 'var(--color-text-muted)', cursor: 'pointer' }}>
              Come generare la coppia di chiavi
            </summary>
            <pre style={{
              marginTop: 8,
              background: 'rgba(0,0,0,0.25)',
              borderRadius: 'var(--radius-sm)',
              padding: '10px 14px',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: '#94a3b8',
              whiteSpace: 'pre',
              overflowX: 'auto',
            }}>{`# Genera un certificato X.509 per la firma CMS di swupdate\nopenssl req -x509 -newkey rsa:4096 -noenc \\\n  -keyout mycert.key.pem \\\n  -out mycert.cert.pem \\\n  -subj "/O=SWUpdate/CN=target" \\\n  -addext extendedKeyUsage=1.3.6.1.5.5.7.3.4 \\\n  -addext keyUsage=digitalSignature\n\n# mycert.key.pem  → chiave privata, usata per firmare i .swu (tienila segreta)\n# mycert.cert.pem → certificato, incollalo qui sopra`}</pre>
          </details>
        </div>
      )}
      {/* ── Support Access — tenant-admin grants platform-admin access to sections ── */}
      {isTenantAdmin && !isPlatformAdmin && tenantId && (
        <SupportAccessSection tenantId={tenantId} />
      )}
    </div>
  )
}

// ── Support Access section ────────────────────────────────────────────────────

function SupportAccessSection({ tenantId }: { tenantId: string }) {
  const { data: grants, isLoading } = useSupportGrants(tenantId)
  const createGrant = useCreateSupportGrant(tenantId)
  const revokeGrant = useRevokeSupportGrant(tenantId)

  const [selectedSections, setSelectedSections] = useState<SupportGrantSection[]>([])
  const [note, setNote] = useState('')
  const [hours, setHours] = useState('24')

  function toggleSection(s: SupportGrantSection) {
    setSelectedSections(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    )
  }

  function handleGrant(e: React.FormEvent) {
    e.preventDefault()
    if (selectedSections.length === 0) return
    const expiresAt = hours
      ? new Date(Date.now() + parseInt(hours) * 3_600_000).toISOString()
      : undefined
    createGrant.mutate(
      { sections: selectedSections, note: note.trim() || undefined, expires_at: expiresAt },
      { onSuccess: () => { setSelectedSections([]); setNote(''); setHours('24') } }
    )
  }

  const activeGrants = (grants ?? []).filter(g => {
    if (g.revoked_at) return false
    if (g.expires_at && new Date(g.expires_at) < new Date()) return false
    return true
  })

  const btnToggle: React.CSSProperties = {
    padding: '5px 12px',
    fontSize: 12,
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    border: '1px solid var(--color-border)',
    background: 'var(--color-surface-2)',
    color: 'var(--color-text-muted)',
    transition: 'all 0.15s',
  }
  const btnToggleActive: React.CSSProperties = {
    ...btnToggle,
    background: 'var(--color-accent-dim)',
    color: 'var(--color-accent)',
    border: '1px solid rgba(0,229,182,0.35)',
  }
  const inputStyle: React.CSSProperties = {
    background: 'var(--color-surface-2)',
    border: '1px solid var(--color-border)',
    color: 'var(--color-text)',
    padding: '7px 10px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
  }

  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius)',
      padding: 20,
      marginBottom: 24,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
        color: 'var(--color-text-muted)', marginBottom: 4,
      }}>
        Platform Support Access
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '0 0 18px' }}>
        Concedi temporaneamente al platform-admin accesso alle sezioni del tuo tenant.
        I grant attivi consentono il supporto; revocali quando il problema è risolto.
      </p>

      {/* New grant form */}
      <form onSubmit={handleGrant} style={{ marginBottom: activeGrants.length > 0 ? 24 : 0 }}>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8, fontWeight: 500 }}>
          Sezioni da sbloccare
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {ALL_SECTIONS.map(s => (
            <button
              key={s.value}
              type="button"
              style={selectedSections.includes(s.value) ? btnToggleActive : btnToggle}
              onClick={() => toggleSection(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              Note (opzionale)
            </label>
            <input
              style={inputStyle}
              placeholder="Describe the issue..."
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              Durata (ore)
            </label>
            <input
              style={inputStyle}
              type="number"
              min="1"
              max="720"
              value={hours}
              onChange={e => setHours(e.target.value)}
            />
          </div>
        </div>
        <button
          type="submit"
          className="btn btn-primary"
          style={{ fontSize: 12 }}
          disabled={selectedSections.length === 0 || createGrant.isPending}
        >
          {createGrant.isPending ? 'Granting…' : 'Grant access'}
        </button>
        {createGrant.isError && (
          <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--color-error)' }}>Failed — try again</span>
        )}
      </form>

      {/* Active grants */}
      {isLoading && <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Loading…</div>}
      {!isLoading && activeGrants.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 12 }}>
          Nessun grant attivo — il platform-admin non ha accesso ai dati di questo tenant.
        </div>
      )}
      {activeGrants.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginTop: 18, marginBottom: 10 }}>
            Grant attivi
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', padding: '4px 0', borderBottom: '1px solid var(--color-border)' }}>Sezioni</th>
                <th style={{ textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', padding: '4px 0', borderBottom: '1px solid var(--color-border)' }}>Note</th>
                <th style={{ textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', padding: '4px 0', borderBottom: '1px solid var(--color-border)' }}>Scade</th>
                <th style={{ borderBottom: '1px solid var(--color-border)' }}></th>
              </tr>
            </thead>
            <tbody>
              {activeGrants.map(g => (
                <tr key={g.id}>
                  <td style={{ padding: '8px 0', paddingRight: 16 }}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {g.sections.map(s => (
                        <span key={s} style={{
                          fontSize: 11, padding: '2px 7px', borderRadius: 3,
                          background: 'var(--color-accent-dim)', color: 'var(--color-accent)',
                          border: '1px solid rgba(0,229,182,0.25)',
                        }}>
                          {ALL_SECTIONS.find(x => x.value === s)?.label ?? s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-muted)', padding: '8px 0', paddingRight: 16 }}>
                    {g.note ?? '—'}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-muted)', padding: '8px 0', whiteSpace: 'nowrap' }}>
                    {g.expires_at ? new Date(g.expires_at).toLocaleString() : 'Until revoked'}
                  </td>
                  <td style={{ padding: '8px 0', textAlign: 'right' }}>
                    <button
                      className="btn btn-ghost"
                      style={{ padding: '3px 10px', fontSize: 11, color: '#ef4444' }}
                      disabled={revokeGrant.isPending}
                      onClick={() => { if (confirm('Revocare questo grant?')) revokeGrant.mutate(g.id) }}
                    >
                      Revoca
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
